import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';

import { WindowRefService } from '@hdms/shared/util-core';

export enum ExpandOnFocusProp {
  WIDTH = 'width',
  HEIGHT = 'height'
}

type EmptyVoidFn = () => void;

export const FOCUSED_CONTAINER_CLASS = 'shrd-input-expand-focused';

// time to store calculated element size after bluring input
// usefull for recurring events and avoiding transition glitches ( e.g. browser tab switch )
const SIZE_CACHE_TIMEOUT = 2000;

@Directive({
  selector: 'input[shrdUicExpandOnFocus]'
})
export class InputExpandOnFocusDirective implements OnInit, OnDestroy {
  // element class selector, which should be treated as focusable child
  // e.g. switching focus to it, will keep focused state on the main element
  @Input() expandOnFocusChildClass: string;
  @Input() expandOnFocusSize: string;
  @Input() expandOnFocusProp = ExpandOnFocusProp.WIDTH;

  private initialSize: number;
  private cacheTimeoutId: number;
  private get window(): Window {
    return this.windowRef.nativeWindow;
  }

  private removeFocusEventListener = () => {
    /* intentionally left blank */
  };
  private removeBlurEventListener = () => {
    /* intentionally left blank */
  };

  constructor(
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly windowRef: WindowRefService
  ) {}

  ngOnInit(): void {
    if (!this.expandOnFocusSize) {
      return;
    }
    this.subscribeHostEvents();
  }

  ngOnDestroy() {
    this.removeFocusEventListener();
    this.removeBlurEventListener();
    this.restoreInputInitialState();
  }

  restoreInputInitialState(sizeCacheTimeout?: number): void {
    this.restoreElInitialState();
    this.clearCachedSize(sizeCacheTimeout);
  }

  /**
   * Subscribe to host element events dynamically,
   * to allow conditinal directive usage without extra event listeners
   */
  private subscribeHostEvents() {
    this.removeFocusEventListener = this.attachEventListener('focus', () => this.onFocus());
    this.removeBlurEventListener = this.attachEventListener('blur', () => this.onBlur());
  }

  /**
   * Attach event through renderer instead of static @HostListener
   */
  private attachEventListener(eventName: string, eventHandler: EmptyVoidFn): EmptyVoidFn {
    return this.renderer.listen(this.elementRef.nativeElement, eventName, () => eventHandler());
  }

  private addContainerClass(className: string): void {
    const el = this.getTargetEl();
    this.renderer.addClass(el, className);
  }

  private clearCachedSize(cachePurgeDelay = 0): void {
    this.cacheTimeoutId = this.window.setTimeout(() => {
      this.initialSize = null;
    }, cachePurgeDelay);
  }

  private getElementCachedSize(): number {
    this.initialSize = this.initialSize || this.getElementSize();
    return this.initialSize;
  }

  /**
   * Get element absolute size value ( width or height )
   * according to configured expand property type
   */
  private getElementSize(): number {
    const el = this.getTargetEl();
    const boundingRect = el.getBoundingClientRect();
    return boundingRect[this.expandOnFocusProp];
  }

  /**
   * Calculate absolute value of expand grow
   * based on passed px or % of own size
   */
  private getExpandPxVal(initialSize: number): number {
    const isPercentageVal = this.expandOnFocusSize.endsWith('%');
    const expandSizeNum = +this.expandOnFocusSize.replace(/%|px/, '');
    return isPercentageVal ? (initialSize * expandSizeNum) / 100 : expandSizeNum;
  }

  private getTargetEl(): HTMLElement {
    return this.renderer.parentNode(this.elementRef.nativeElement);
  }

  private onBlur(): void {
    // Use timeout to delay examination of activeElement until blur => focus have been processed
    this.window.setTimeout(() => {
      const doc = this.window.document;
      const focusableChild = this.expandOnFocusChildClass
        ? doc.querySelector(`.${this.expandOnFocusChildClass}`)
        : null;
      const isFocusInChild = focusableChild && doc.activeElement === focusableChild;
      if (!isFocusInChild) {
        this.restoreInputInitialState(SIZE_CACHE_TIMEOUT);
      }
    });
  }

  private onFocus(): void {
    this.addContainerClass(FOCUSED_CONTAINER_CLASS);
    this.setElExpandedSize();
    this.window.clearTimeout(this.cacheTimeoutId);
  }

  private removeContainerClass(className: string): void {
    const el = this.getTargetEl();
    this.renderer.removeClass(el, className);
  }

  private restoreElInitialState(): void {
    this.removeContainerClass(FOCUSED_CONTAINER_CLASS);
    this.setElementSize(null);
  }

  private setElExpandedSize(): void {
    const initialPropSize = this.getElementCachedSize();
    const expandPxVal = this.getExpandPxVal(initialPropSize);
    this.setElementSize(`${initialPropSize + expandPxVal}px`);
  }

  private setElementSize(size: string): void {
    const el = this.getTargetEl();
    this.renderer.setStyle(el, this.expandOnFocusProp, size);
  }
}
